/* eslint-disable camelcase */
import { getCustomVars, resetCustomVars } from "./customVars";
import {
  Page,
  Click,
  Publisher,
  PageTrackingProps,
  PublisherTrackingProps,
  PublisherEventType,
  CustomVars,
} from "@companion-core/shared/app/Interfaces/tracking";
import { isWeb } from "@companion-core/shared/app/Utils/platform";

type TrackingParams = Page | Click | Publisher;
type UserTrackingParams = [string, string, string?, boolean?];

const getAuthParamForPayload = (customVars: CustomVars) => {
  const { authentification, ...restCustomVars } = customVars;
  const authParam = isWeb()
    ? { authentification: authentification ?? 2 }
    : { authentification_1: authentification ?? 2 };
  return { restCustomVars, authParam, authentification };
};

let track: (event: string, params: TrackingParams) => void = () => {
  console.warn("track method not initialized yet");
};

let pendingUserCall: UserTrackingParams | null = null;
let latestUserCall: UserTrackingParams | null = null;

let setUserImplementation: (
  userId: string,
  subscriptionsId: string,
  userCategory?: string,
  storeId?: boolean
) => void = (userId, subscriptionsId, userCategory, storeId) => {
  pendingUserCall = [userId, subscriptionsId, userCategory, storeId];
};

export const setUser = (
  userId: string,
  subscriptionsId: string,
  userCategory?: string,
  storeId?: boolean
) => {
  latestUserCall = [userId, subscriptionsId, userCategory, storeId];
  setUserImplementation(...latestUserCall);
};

export const replayLastUser = () => {
  if (latestUserCall) {
    setUserImplementation(...latestUserCall);
  }
};

export const setTrackCallback = (callback: (event: string, params: TrackingParams) => void) => {
  track = callback;
};

export const setUserCallback = (
  callback: (
    userId: string,
    subscriptionsId: string,
    userCategory?: string,
    storeId?: boolean
  ) => void
) => {
  setUserImplementation = callback;
  if (pendingUserCall) {
    callback(...pendingUserCall);
    pendingUserCall = null;
  }
};

export const trackPage = ({
  page = "", // Could not be undefined because we are tracking a page
  page_chapter1 = "",
  page_chapter2 = "",
  page_chapter3 = "",
  site_level2 = 0,
  vendorId = "",
  contentId = "",
  requestId = "",
}: PageTrackingProps): Page => {
  const customVars = getCustomVars();
  customVars.referrer = customVars.url;
  customVars.url = isWeb() ? window?.location?.href : ""; // In mobile url field should be empty

  if (vendorId) {
    customVars.vendor_id = vendorId;
  }
  if (contentId) {
    customVars.offer_id = contentId;
  }
  if (requestId) {
    customVars.case_UUID = requestId;
  }

  const { restCustomVars, authParam, authentification } = getAuthParamForPayload(customVars);

  const payload: Page = {
    page,
    page_chapter1,
    page_chapter2,
    page_chapter3,
    site_level2,
    ...restCustomVars,
    ...authParam,
  };

  track("page.display", payload);

  const { subscription_UUID, url } = customVars;
  resetCustomVars({ subscription_UUID, url, authentification });

  return payload;
};

export const trackClick = ({
  click_type = "",
  click = "",
  click_chapter1 = "",
  click_chapter2 = "",
  click_chapter3 = "",
  site_level2 = 0,
  vendorId = "",
  contentId = "",
  requestId = "",
  referrer = "",
}: Click): Click => {
  const customVars = getCustomVars();
  if (vendorId) {
    customVars.vendor_id = vendorId;
  }
  if (contentId) {
    customVars.offer_id = contentId;
  }
  if (requestId) {
    customVars.case_UUID = requestId;
  }
  if (referrer) {
    customVars.referrer = referrer;
  }
  const { restCustomVars, authParam, authentification } = getAuthParamForPayload(customVars);

  const payload: Click = {
    click_type,
    click,
    click_chapter1,
    click_chapter2,
    click_chapter3,
    site_level2,
    ...restCustomVars,
    ...authParam,
  };

  track("click.navigation", payload);

  const { subscription_UUID } = customVars;
  resetCustomVars({ subscription_UUID, authentification });

  return payload;
};

export const trackPublisher = (
  type: PublisherEventType,
  {
    onsitead_campaign = "",
    onsitead_creation = "",
    onsitead_variant = "",
    onsitead_format = "",
    onsitead_general_placement = "",
    onsitead_detailed_placement = "",
    onsitead_advertiser = "",
    onsitead_url = "",
  }: PublisherTrackingProps
): Publisher => {
  const payload: Publisher = {
    onsitead_campaign,
    onsitead_creation,
    onsitead_variant,
    onsitead_format,
    onsitead_general_placement,
    onsitead_detailed_placement,
    onsitead_advertiser,
    onsitead_url,
  };

  track(type, payload);

  return payload;
};
